import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { supabase } from './supabaseClient';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import Checkbox from '@mui/material/Checkbox';

const fieldProps = { size: 'medium', fullWidth: true };

/** Keeps number field text vertically centered with outlined inputs (esp. with spin buttons). */
const alignedNumberFieldSx = {
  '& .MuiOutlinedInput-root': { alignItems: 'center' },
  '& .MuiOutlinedInput-input': {
    py: '10px',
    lineHeight: 1.5,
    boxSizing: 'border-box',
    height: 'auto'
  }
};

function parsePctInput(value) {
  if (value === undefined || value === null) return null;
  const s = String(value).trim().replace(',', '.');
  if (s === '') return null;
  const n = parseFloat(s);
  if (Number.isNaN(n)) return null;
  return Math.min(100, Math.max(0, n));
}

function formatSupabaseError(error) {
  if (!error) return 'Unknown error';
  const parts = [error.message, error.details, error.hint].filter(Boolean);
  return parts.length ? parts.join(' — ') : JSON.stringify(error);
}

function memberAvailableAmount(memberName, netMargin, distData, draftMap, memberRow) {
  const raw =
    draftMap[memberName] !== undefined
      ? draftMap[memberName]
      : String(memberRow?.distribution_percentage ?? 0);
  const pct = parsePctInput(raw);
  const p = pct === null ? 0 : pct;
  const paid = distData
    .filter((d) => d.member_name === memberName)
    .reduce((s, d) => s + (d.amount || 0), 0);
  return netMargin * (p / 100) - paid;
}

function AccountPage() {
  const [totals, setTotals] = useState({
    topPeakMargin: 0,
    unpaidRainBirdAmount: 0,
    unpaidToPBSAmount: 0,
    accountBalance: 0,
    projectCostFundNet: 0,
    projectCostTarget: 0,
    projectCostSpent: 0
  });

  const [members, setMembers] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [memberPctDraft, setMemberPctDraft] = useState({});
  const [projectCostPctStr, setProjectCostPctStr] = useState('10');
  const [selectedMember, setSelectedMember] = useState('');
  const [distributionAmount, setDistributionAmount] = useState('');
  const [distributionNote, setDistributionNote] = useState('');
  const [selectedDistId, setSelectedDistId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [distributionDate, setDistributionDate] = useState('');
  const [savingMemberPct, setSavingMemberPct] = useState(false);
  const [savingProjectPct, setSavingProjectPct] = useState(false);

  /** Latest project cost % text — fetchData uses a stable callback; ref avoids stale totals when DB row is missing. */
  const projectCostPctStrRef = useRef(projectCostPctStr);
  useEffect(() => {
    projectCostPctStrRef.current = projectCostPctStr;
  }, [projectCostPctStr]);

  const fetchData = useCallback(async (opts) => {
    if (!supabase) {
      toast.error('Supabase is not configured.');
      return;
    }

    const savedProjectCostPct = opts?.savedProjectCostPct;

    const { data: txData, error: txError } = await supabase.from('transactions').select('*');
    if (txError) return toast.error('Transaction error: ' + txError.message);

    const { data: memberData, error: memberError } = await supabase.from('members').select('*');
    if (memberError) return toast.error('Members error: ' + memberError.message);

    const { data: distData, error: distError } = await supabase.from('distributions').select('*');
    if (distError) return toast.error('Distributions error: ' + distError.message);

    const { data: settingsRow, error: settingsError } = await supabase
      .from('account_settings')
      .select('project_cost_percentage')
      .eq('id', 1)
      .maybeSingle();

    if (settingsError && settingsError.code !== 'PGRST116') {
      console.warn('account_settings:', settingsError.message);
    }

    const dbPct =
      settingsRow?.project_cost_percentage != null && !Number.isNaN(Number(settingsRow.project_cost_percentage))
        ? Number(settingsRow.project_cost_percentage)
        : null;

    const projectCostPct =
      savedProjectCostPct != null && Number.isFinite(savedProjectCostPct)
        ? savedProjectCostPct
        : dbPct !== null
          ? dbPct
          : parsePctInput(projectCostPctStrRef.current) ?? 10;

    setProjectCostPctStr((prev) => {
      if (settingsRow?.project_cost_percentage != null && !Number.isNaN(Number(settingsRow.project_cost_percentage))) {
        return String(Number(settingsRow.project_cost_percentage));
      }
      if (savedProjectCostPct != null && Number.isFinite(savedProjectCostPct)) {
        return String(savedProjectCostPct);
      }
      return prev;
    });

    const marginIn = txData
      .filter((tx) => tx.from_who === 'Rain Bird US' && tx.to_who === 'Top Peak IT' && !tx.project_cost)
      .reduce((sum, tx) => sum + (tx.amount || 0), 0);
    const marginOut = txData
      .filter((tx) => tx.from_who === 'Top Peak IT' && tx.to_who === 'PBS' && !tx.project_cost)
      .reduce((sum, tx) => sum + (tx.amount || 0), 0);
    const netMargin = marginIn - marginOut;

    const unpaidRainBird = txData
      .filter((tx) => !tx.paid && tx.from_who === 'Rain Bird US' && tx.to_who === 'Top Peak IT')
      .reduce((sum, tx) => sum + (tx.amount || 0), 0);
    const unpaidToPBS = txData
      .filter((tx) => !tx.paid && tx.from_who === 'Top Peak IT' && tx.to_who === 'PBS')
      .reduce((sum, tx) => sum + (tx.amount || 0), 0);

    const paidIn = txData
      .filter((tx) => tx.paid && tx.from_who === 'Rain Bird US' && tx.to_who === 'Top Peak IT')
      .reduce((sum, tx) => sum + (tx.amount || 0), 0);
    const paidOut = txData
      .filter((tx) => tx.paid && tx.from_who === 'Top Peak IT' && tx.to_who === 'PBS')
      .reduce((sum, tx) => sum + (tx.amount || 0), 0);
    const totalDistributed = distData.reduce((sum, d) => sum + (d.amount || 0), 0);
    const accountBalance = paidIn - paidOut - totalDistributed;

    const projectCostTarget = netMargin * (projectCostPct / 100);
    const projectCostSpent = txData
      .filter((tx) => tx.project_cost)
      .reduce((sum, tx) => sum + (tx.amount || 0), 0);
    const projectCostFundNet = projectCostTarget - projectCostSpent;

    const updatedMembers = memberData.map((member) => {
      const paidOutDist = distData
        .filter((d) => d.member_name === member.member_name)
        .reduce((sum, d) => sum + (d.amount || 0), 0);
      const share = netMargin * ((Number(member.distribution_percentage) || 0) / 100);
      const available = share - paidOutDist;
      return { ...member, distribution_available: available };
    });

    setTotals({
      topPeakMargin: netMargin,
      unpaidRainBirdAmount: unpaidRainBird,
      unpaidToPBSAmount: unpaidToPBS,
      accountBalance,
      projectCostFundNet,
      projectCostTarget,
      projectCostSpent
    });
    setMembers(updatedMembers);
    setDistributions(distData);

    setMemberPctDraft(
      Object.fromEntries(
        memberData.map((m) => [m.member_name, String(Number(m.distribution_percentage) || 0)])
      )
    );

    for (const member of updatedMembers) {
      await supabase
        .from('members')
        .update({ distribution_available: member.distribution_available })
        .eq('member_name', member.member_name);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const memberPctSum = useMemo(() => {
    return members.reduce((sum, m) => {
      const v = parsePctInput(memberPctDraft[m.member_name] ?? '0');
      return sum + (v === null ? 0 : v);
    }, 0);
  }, [members, memberPctDraft]);

  const handleMemberPctChange = (memberName, value) => {
    setMemberPctDraft((prev) => ({ ...prev, [memberName]: value }));
  };

  const saveMemberPercentages = async () => {
    const parsed = members.map((m) => {
      const p = parsePctInput(memberPctDraft[m.member_name]);
      if (p === null) {
        return { error: `Invalid percentage for ${m.member_name}` };
      }
      return { member: m, pct: p };
    });
    const bad = parsed.find((x) => x.error);
    if (bad?.error) {
      toast.error(bad.error);
      return;
    }

    const sum = parsed.reduce((s, x) => s + x.pct, 0);
    if (sum > 100 + 1e-6) {
      toast.error(`Total member percentages (${sum.toFixed(2)}%) cannot exceed 100%.`);
      return;
    }

    setSavingMemberPct(true);
    try {
      for (const { member, pct } of parsed) {
        const res =
          member.id != null
            ? await supabase.from('members').update({ distribution_percentage: pct }).eq('id', member.id)
            : await supabase
                .from('members')
                .update({ distribution_percentage: pct })
                .eq('member_name', member.member_name);
        if (res.error) {
          toast.error(`Failed to update ${member.member_name}: ${formatSupabaseError(res.error)}`);
          setSavingMemberPct(false);
          return;
        }
      }
      toast.success('Member distribution percentages saved.');
      await fetchData();
    } finally {
      setSavingMemberPct(false);
    }
  };

  const saveProjectCostPercentage = async () => {
    if (!supabase) {
      toast.error('Supabase is not configured.');
      return;
    }

    const rawStr = projectCostPctStr ?? '';
    const parsed = parsePctInput(rawStr);
    if (parsed === null) {
      toast.error('Enter a valid project cost fund percentage (0–100).');
      return;
    }

    const valueToSave = Number(parsed);
    if (!Number.isFinite(valueToSave)) {
      toast.error('Enter a valid project cost fund percentage (0–100).');
      return;
    }

    const handleDbError = (err) => {
      if (!err) return false;
      if (err.message?.includes('relation') || err.code === '42P01') {
        toast.error(
          'Database table account_settings is missing. Run supabase/sql/account_settings.sql in the Supabase SQL editor, then try again.'
        );
        return true;
      }
      if (
        err.code === '42501' ||
        err.message?.toLowerCase().includes('permission') ||
        err.message?.toLowerCase().includes('policy')
      ) {
        toast.error(
          'Saving was blocked (likely RLS). Re-run the full supabase/sql/account_settings.sql in the Supabase SQL editor (policies must allow anon if you use the anon key without login).'
        );
        return true;
      }
      toast.error('Could not save project cost %: ' + formatSupabaseError(err));
      return true;
    };

    setSavingProjectPct(true);
    try {
      const { data: updated, error: updateError } = await supabase
        .from('account_settings')
        .update({ project_cost_percentage: valueToSave })
        .eq('id', 1)
        .select('project_cost_percentage')
        .maybeSingle();

      if (handleDbError(updateError)) return;

      if (updated) {
        await fetchData({ savedProjectCostPct: valueToSave });
        toast.success('Project cost fund percentage saved.');
        return;
      }

      const { data: inserted, error: insertError } = await supabase
        .from('account_settings')
        .insert({ id: 1, project_cost_percentage: valueToSave })
        .select('project_cost_percentage')
        .maybeSingle();

      if (insertError) {
        if (insertError.code === '23505') {
          const { error: retryErr } = await supabase
            .from('account_settings')
            .update({ project_cost_percentage: valueToSave })
            .eq('id', 1)
            .select('project_cost_percentage')
            .maybeSingle();
          if (handleDbError(retryErr)) return;
          await fetchData({ savedProjectCostPct: valueToSave });
          toast.success('Project cost fund percentage saved.');
          return;
        }
        handleDbError(insertError);
        return;
      }

      if (inserted) {
        await fetchData({ savedProjectCostPct: valueToSave });
        toast.success('Project cost fund percentage saved.');
        return;
      }

      await fetchData({ savedProjectCostPct: valueToSave });
      toast.success('Project cost fund percentage saved.');
    } finally {
      setSavingProjectPct(false);
    }
  };

  return (
    <Stack spacing={3}>
      <ToastContainer position="top-right" autoClose={3000} />
      <Typography variant="h5" component="h2" fontWeight={700}>
        Accounts
      </Typography>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Total margin to date
        </Typography>
        <Typography variant="h6">${totals.topPeakMargin.toFixed(2)}</Typography>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Unpaid summaries
        </Typography>
        <Stack spacing={0.5}>
          <Typography variant="body2">
            <strong>Unpaid from Rain Bird US:</strong> ${(totals.unpaidRainBirdAmount ?? 0).toFixed(2)}
          </Typography>
          <Typography variant="body2">
            <strong>Unpaid to PBS:</strong> ${(totals.unpaidToPBSAmount ?? 0).toFixed(2)}
          </Typography>
          <Typography variant="body2">
            <strong>Total currently in account:</strong> ${(totals.accountBalance ?? 0).toFixed(2)}
          </Typography>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Project cost fund
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set what share of <strong>net margin</strong> is reserved for the project cost pool (previously fixed at 10%).
        </Typography>
        <Box sx={{ maxWidth: { sm: 560 } }}>
          <Typography
            variant="body2"
            component="label"
            htmlFor="project-cost-pct"
            sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}
          >
            Project cost % of margin
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              id="project-cost-pct"
              size="medium"
              hiddenLabel
              fullWidth
              type="number"
              value={projectCostPctStr ?? ''}
              onChange={(e) => setProjectCostPctStr(e.target.value)}
              inputProps={{ min: 0, max: 100, step: 0.1, 'aria-describedby': 'project-cost-pct-helper' }}
              placeholder="e.g. 10"
              sx={{ flex: 1, minWidth: 0, ...alignedNumberFieldSx }}
            />
            <Button
              type="button"
              variant="contained"
              onClick={saveProjectCostPercentage}
              disabled={savingProjectPct}
              sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'auto' } }}
            >
              {savingProjectPct ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
          <Typography id="project-cost-pct-helper" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Enter 0–100 (share of net margin for the project cost pool).
          </Typography>
        </Box>
        <Stack spacing={0.5} sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Target pool ({parsePctInput(projectCostPctStr) ?? '—'}% of margin):</strong> $
            {(totals.projectCostTarget ?? 0).toFixed(2)}
          </Typography>
          <Typography variant="body2">
            <strong>Spent (project-cost transactions):</strong> ${(totals.projectCostSpent ?? 0).toFixed(2)}
          </Typography>
          <Typography variant="body1">
            <strong>Funds available:</strong> ${(totals.projectCostFundNet ?? 0).toFixed(2)}
          </Typography>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Distributions available
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Edit each member&apos;s share of net margin. Individual percentages are 0–100; the <strong>total cannot exceed 100%</strong>.
          Available updates from your draft values before you save.
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Member</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Distribution %</TableCell>
                <TableCell>Available (preview)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>No members found or calculated yet.</TableCell>
                </TableRow>
              )}
              {members.map((m, index) => {
                const avail = memberAvailableAmount(
                  m.member_name,
                  totals.topPeakMargin,
                  distributions,
                  memberPctDraft,
                  m
                );
                return (
                  <TableRow
                    key={m.id || m.member_name}
                    sx={{
                      bgcolor: index % 2 === 1 ? '#f9f9f9' : 'white',
                      borderBottom: '1px solid #e0e0e0'
                    }}
                  >
                    <TableCell>{m.member_name}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={memberPctDraft[m.member_name] ?? ''}
                        onChange={(e) => handleMemberPctChange(m.member_name, e.target.value)}
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                        sx={{
                          maxWidth: 120,
                          '& .MuiOutlinedInput-root': { alignItems: 'center' },
                          '& .MuiOutlinedInput-input': {
                            py: '8px',
                            lineHeight: 1.5,
                            height: 'auto',
                            boxSizing: 'border-box'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>${avail.toFixed(2)}</TableCell>
                  </TableRow>
                );
              })}
              {members.length > 0 && (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={memberPctSum > 100 ? 'error' : 'text.primary'}
                    >
                      Total: {memberPctSum.toFixed(2)}%{memberPctSum > 100 ? ' — over 100%, cannot save' : ''}
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={saveMemberPercentages}
            disabled={savingMemberPct || memberPctSum > 100 + 1e-6}
          >
            {savingMemberPct ? 'Saving…' : 'Save member percentages'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Distribute now
        </Typography>
        <Box
          component="form"
          onSubmit={async (e) => {
            e.preventDefault();
            const member = members.find((m) => m.member_name === selectedMember);
            const amount = parseFloat(distributionAmount);

            if (!member) return toast.error('Please select a valid member.');
            if (isNaN(amount) || amount <= 0) return toast.error('Enter a valid distribution amount.');

            const available = memberAvailableAmount(
              selectedMember,
              totals.topPeakMargin,
              distributions,
              memberPctDraft,
              member
            );
            if (amount > available) {
              return toast.error(`Amount exceeds available: $${available.toFixed(2)}`);
            }

            if (editMode && selectedDistId) {
              const { error } = await supabase
                .from('distributions')
                .update({
                  member_name: selectedMember,
                  amount,
                  distribution_date: distributionDate || new Date().toISOString().slice(0, 10),
                  notes: distributionNote
                })
                .eq('id', selectedDistId);

              if (error) toast.error('Update failed: ' + error.message);
              else {
                toast.success(`Updated distribution for ${selectedMember}`);
                setSelectedDistId(null);
                setEditMode(false);
              }
            } else {
              const { error } = await supabase.from('distributions').insert([
                {
                  member_name: member.member_name,
                  amount,
                  distribution_date: distributionDate || new Date().toISOString().slice(0, 10),
                  notes: distributionNote
                }
              ]);

              if (error) toast.error('Insert failed: ' + error.message);
              else toast.success(`Distributed $${amount.toFixed(2)} to ${member.member_name}`);
            }

            setSelectedMember('');
            setDistributionAmount('');
            setDistributionNote('');
            fetchData();
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                {...fieldProps}
                select
                label="Member"
                value={selectedMember}
                onChange={(e) => setSelectedMember(e.target.value)}
              >
                <MenuItem value="">Select</MenuItem>
                {members.map((m) => (
                  <MenuItem key={m.member_name} value={m.member_name}>
                    {m.member_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                {...fieldProps}
                type="number"
                label="Amount"
                value={distributionAmount}
                onChange={(e) => setDistributionAmount(e.target.value)}
                placeholder="Amount to distribute"
                inputProps={{ min: 0, step: 0.01 }}
                sx={alignedNumberFieldSx}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                {...fieldProps}
                type="date"
                label="Distribution date"
                value={distributionDate}
                onChange={(e) => setDistributionDate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                {...fieldProps}
                label="Notes"
                value={distributionNote}
                onChange={(e) => setDistributionNote(e.target.value)}
                placeholder="Optional note"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button type="submit" variant="contained">
                {editMode ? 'Update' : 'Distribute now'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Distributions history
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">Select</TableCell>
                <TableCell>Member</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {distributions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>No distributions found.</TableCell>
                </TableRow>
              )}
              {distributions.map((dist, index) => (
                <TableRow
                  key={dist.id}
                  sx={{
                    bgcolor:
                      selectedDistId === dist.id ? '#eef6ff' : index % 2 === 1 ? '#f9f9f9' : 'white',
                    borderBottom: '1px solid #e0e0e0'
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedDistId === dist.id}
                      onChange={() => setSelectedDistId(selectedDistId === dist.id ? null : dist.id)}
                    />
                  </TableCell>
                  <TableCell>{dist.member_name}</TableCell>
                  <TableCell>${dist.amount.toFixed(2)}</TableCell>
                  <TableCell>{dist.distribution_date}</TableCell>
                  <TableCell>{dist.notes}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {selectedDistId && (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              onClick={() => {
                const dist = distributions.find((d) => d.id === selectedDistId);
                if (dist) {
                  setSelectedMember(dist.member_name);
                  setDistributionAmount(dist.amount.toString());
                  setDistributionNote(dist.notes || '');
                  setDistributionDate(dist.distribution_date || '');
                  setEditMode(true);
                }
              }}
            >
              Edit selected
            </Button>

            <Button
              color="error"
              variant="outlined"
              onClick={async () => {
                if (window.confirm('Are you sure you want to delete this distribution?')) {
                  const { error } = await supabase.from('distributions').delete().eq('id', selectedDistId);
                  if (error) toast.error('Delete failed: ' + error.message);
                  else {
                    toast.success('Distribution deleted.');
                    setSelectedDistId(null);
                    fetchData();
                  }
                }
              }}
            >
              Delete selected
            </Button>
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}

export default AccountPage;
